/**
 * EARNED SCHEDULE — src/earned-schedule.ts   (Metrics Layer C, the schedule half)
 *
 *   npx tsx scripts/test-earned-schedule.ts
 *
 * The date-truth read FlightDeck names and owns. The classic schedule index SPI =
 * EV ÷ PV (earnedValue in portfolio.ts) is a MONEY ratio: it drifts to 1.0 as a
 * project ends even when the finish date is slipping, so it must NEVER govern the
 * date (finding #4 / L183). Earned Schedule asks the honest question instead —
 *
 *   "At what point in the PLAN was today's delivered value supposed to be reached?"
 *
 * That point is ES (in time). Compared with the time actually elapsed (AT), it gives
 * a schedule index in TIME units, SPI(t) = ES ÷ AT, which tells the truth right up to
 * the end. The schedule forecast is then  planned duration ÷ SPI(t).
 *
 * ES is read off the PLAN'S OWN cumulative value curve — the size-weighted share of
 * the work each planned finish date was meant to deliver — not a straight line, so a
 * front- or back-loaded plan is read as it really is (the mock's plan reaches 41% of
 * value only at planned week 12.1 of 20, not week 8.2). The curve spans the SCHEDULED
 * cards only (those with a planned due + a size), the same dated-subgraph honesty the
 * critical path keeps; undated work is Layer A's to chase, not this read's to guess.
 *
 * Pure, and parity-safe by construction: it takes the plan points and today's earned
 * % as INPUTS (the caller builds them from the SAME cards and weights earnedValue
 * earns against), so the ES read can never weigh the work differently from the EVM
 * read beside it. Proved by scripts/test-earned-schedule.ts.
 */
import { workingDaysBetween, addWorkingDays, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';
import type { Rag } from './portfolio';

/** One scheduled card as the value curve reads it: the day it is PLANNED to finish,
 *  and its size weight (the same weight earnedValue earns against). */
export interface PlanPoint {
  due: string;
  weight: number;
}

export interface EarnedScheduleInput {
  /** The scheduled cards (planned due + weight). Undated cards are deliberately NOT
   *  here — they carry no planned time to read value against. */
  plan: PlanPoint[];
  /** Today's earned value as a percent of the baseline, 0–100 — the SAME number the
   *  EVM read earns at, passed in so the two cannot diverge. */
  earnedPct: number;
  /** The project's planned start day (YYYY-MM-DD). */
  startISO: string;
  /** The FROZEN committed finish (baseline go-live) — planned duration is measured to
   *  here, never to a moving forecast. */
  goLiveISO: string;
  now: number;
  cal?: WorkCalendar;
}

export interface EarnedSchedule {
  /** Earned Schedule: the planned elapsed time, in weeks, at which the plan's value
   *  curve reaches today's earned %. */
  esWeeks: number;
  /** Actual Time elapsed, in weeks. */
  atWeeks: number;
  /** Planned duration start→go-live, in weeks. */
  plannedWeeks: number;
  /** The time-based schedule index ES ÷ AT. Null when there is no elapsed time or no
   *  scheduled work to read. Below 1.0 = behind, above = ahead. */
  spiT: number | null;
  /** The schedule forecast, planned duration ÷ SPI(t), in weeks. Null with no SPI(t). */
  forecastWeeks: number | null;
  /** The forecast finish day, or null with no SPI(t). */
  forecastFinishISO: string | null;
  /** Weeks late (positive) or early (negative) against the plan. Null with no SPI(t). */
  latenessWeeks: number | null;
  /** On-schedule / slipping / behind on SPI(t), the same bands the EVM read uses
   *  (≥0.95 green, ≥0.85 amber, else red). Null when there is no SPI(t). */
  rag: Rag | null;
  /** How many plan points fed the curve — the honest "reads N scheduled cards". */
  scheduled: number;
}

/** The YYYY-MM-DD as local-midnight ms (matches work-calendar's own day handling). */
function localMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getTime();
}

/** ≥0.95 good, ≥0.85 drifting, else off — the shared EVM tolerance, so this read and
 *  the cost read grade on one scale. */
function indexRag(index: number): Rag {
  if (index >= 0.95) return 'green';
  if (index >= 0.85) return 'amber';
  return 'red';
}

/**
 * The working days from the project start at which the plan's cumulative value curve
 * reaches `targetWeight`, interpolated linearly between the two bracketing planned
 * finishes (so ES is fractional, like the mock's 12.1 weeks). 0 at no value; the last
 * planned finish when the target is beyond the whole plan.
 */
function earnedScheduleWorkingDays(
  sorted: PlanPoint[],
  targetWeight: number,
  startMs: number,
  cal: WorkCalendar,
): number {
  if (targetWeight <= 0) return 0;
  let cumPrev = 0;
  let wPrev = 0; // working days from start to the previous point (the start itself = 0)
  for (const p of sorted) {
    const cumThis = cumPrev + p.weight;
    const wThis = workingDaysBetween(startMs, localMs(p.due), cal);
    if (cumThis >= targetWeight) {
      const span = cumThis - cumPrev;
      const f = span > 0 ? (targetWeight - cumPrev) / span : 1;
      return wPrev + f * (wThis - wPrev);
    }
    cumPrev = cumThis;
    wPrev = wThis;
  }
  return wPrev; // earned more than the whole plan accounts for — pin to the last finish
}

/**
 * Compute the Earned Schedule read for a project.
 */
export function earnedSchedule(input: EarnedScheduleInput): EarnedSchedule {
  const cal = input.cal ?? DEFAULT_CALENDAR;
  const perWeek = cal.week && cal.week.length ? cal.week.length : 5;
  const startMs = localMs(input.startISO);
  const total = input.plan.reduce((s, p) => s + Math.max(0, p.weight), 0);

  const atWorkingDays = workingDaysBetween(startMs, input.now, cal);
  const plannedWorkingDays = workingDaysBetween(startMs, localMs(input.goLiveISO), cal);
  const atWeeks = atWorkingDays / perWeek;
  const plannedWeeks = plannedWorkingDays / perWeek;

  const base: EarnedSchedule = {
    esWeeks: 0,
    atWeeks,
    plannedWeeks,
    spiT: null,
    forecastWeeks: null,
    forecastFinishISO: null,
    latenessWeeks: null,
    rag: null,
    scheduled: input.plan.length,
  };

  // No scheduled work, or no time elapsed yet — there is nothing to read a time index
  // from. Return the elapsed/planned frame with a null index rather than invent one.
  if (total <= 0 || atWorkingDays <= 0) return base;

  const sorted = [...input.plan].sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0));
  const targetWeight = (Math.max(0, Math.min(100, input.earnedPct)) / 100) * total;
  const esWorkingDays = earnedScheduleWorkingDays(sorted, targetWeight, startMs, cal);

  const spiT = esWorkingDays / atWorkingDays;
  const forecastWorkingDays = spiT > 0 ? plannedWorkingDays / spiT : null;

  return {
    ...base,
    esWeeks: esWorkingDays / perWeek,
    spiT,
    forecastWeeks: forecastWorkingDays != null ? forecastWorkingDays / perWeek : null,
    forecastFinishISO: forecastWorkingDays != null ? addWorkingDays(input.startISO, Math.round(forecastWorkingDays), cal) : null,
    latenessWeeks: forecastWorkingDays != null ? (forecastWorkingDays - plannedWorkingDays) / perWeek : null,
    rag: indexRag(spiT),
  };
}
